import { z } from "zod";
import { IsoDateSchema, IsoDateTimeSchema } from "./primitives.js";
export const GoalTypeSchema = z.enum(["maintain", "loss", "gain", "improve_health"]);
export const CookingEnjoymentSchema = z.enum(["low", "medium", "high"]);
export const CookingSkillSchema = z.enum(["beginner", "intermediate", "advanced"]);
export const PlanningModeSchema = z.enum(["daily", "batch", "hybrid"]);
/**
 * Meal slots, in chronological day order (ADR-023 snack slots).
 * `morning_snack` sits between breakfast and lunch, `afternoon_snack`
 * between lunch and dinner. The legacy generic `snack` value is kept for
 * wire/DB backward compatibility (pre-ADR-023 rows and schedules) — new
 * plans only ever emit the two positioned snack slots.
 */
export const MealSlotSchema = z.enum([
    "breakfast",
    "morning_snack",
    "lunch",
    "afternoon_snack",
    "dinner",
    "snack",
]);
/**
 * ADR-023: user-level snack timing preference. Captured in onboarding step
 * 10 (meal structure) and editable via PATCH /v1/profile. Default `none` —
 * existing users keep their exact pre-snack plan behavior until they opt in.
 * Takes effect on the NEXT plan generation, never by silently mutating the
 * current plan.
 */
export const SnackPreferenceSchema = z.enum(["none", "morning", "afternoon", "both"]);
// ---------------------------------------------------------------------------
// Personal information (2026-08 profile batch; migration 0021, ADR-024).
// Identity-adjacent fields stored on `users` (NOT `profiles`), all nullable —
// existing users have none of them and the UI degrades to the email prefix
// plus a non-blocking "complete your profile" prompt.
// ---------------------------------------------------------------------------
/**
 * Light E.164-ish phone validation: optional leading `+`, then digits with
 * incidental spaces/dashes/parentheses, 6–15 digits total. The server strips
 * separators and stores the normalized form.
 */
export const PhoneSchema = z
    .string()
    .trim()
    .regex(/^\+?[\d ()-]{6,20}$/, "not a plausible phone number")
    .refine((v) => {
    const digits = v.replace(/\D/g, "");
    return digits.length >= 6 && digits.length <= 15;
}, "phone must contain 6-15 digits");
/** Age bounds derived from date_of_birth: 18+ product, 120-year sanity cap. */
export const DOB_MIN_AGE_YEARS = 18;
export const DOB_MAX_AGE_YEARS = 120;
export function ageOnDate(dateOfBirth, on = new Date()) {
    const dob = new Date(`${dateOfBirth}T00:00:00Z`);
    let age = on.getUTCFullYear() - dob.getUTCFullYear();
    const monthDelta = on.getUTCMonth() - dob.getUTCMonth();
    if (monthDelta < 0 || (monthDelta === 0 && on.getUTCDate() < dob.getUTCDate()))
        age -= 1;
    return age;
}
/**
 * GET /v1/profile `personalInfo`. `email` is the Cognito sign-in alias and is
 * read-only here (display only; not accepted by PATCH). `photoUrl` is a
 * short-lived presigned GET URL for the private avatar object, or null — the
 * raw S3 key is never exposed on reads.
 */
export const PersonalInfoSchema = z.object({
    firstName: z.string().nullable(),
    familyName: z.string().nullable(),
    /** YYYY-MM-DD */
    dateOfBirth: IsoDateSchema.nullable(),
    email: z.string(),
    phone: z.string().nullable(),
    photoUrl: z.string().nullable(),
});
/**
 * PATCH /v1/profile `personalInfo` — explicit editable fields only. Email is
 * deliberately absent (Cognito sign-in alias). `photoKey` confirms a
 * completed avatar upload (must be the key returned by
 * POST /v1/profile/photo-upload for THIS user; the server rejects foreign
 * prefixes) or `null` to remove the photo (server deletes the S3 object).
 * DOB age bounds (18–120) are re-enforced server-side; the refine here gives
 * clients early, honest feedback with the same rule.
 */
export const PersonalInfoPatchSchema = z.object({
    firstName: z.string().trim().min(1).max(100).optional(),
    familyName: z.string().trim().min(1).max(100).optional(),
    dateOfBirth: IsoDateSchema.refine((v) => {
        const age = ageOnDate(v);
        return age >= DOB_MIN_AGE_YEARS && age <= DOB_MAX_AGE_YEARS;
    }, { message: `date of birth must imply an age of ${DOB_MIN_AGE_YEARS}-${DOB_MAX_AGE_YEARS}` }).optional(),
    phone: PhoneSchema.nullable().optional(),
    // Exactly the shape the photo-upload endpoint mints — no traversal
    // segments, no foreign prefixes (2026-08-25 review finding 4: the prior
    // free-form string relied on S3 treating `..` literally).
    photoKey: z
        .string()
        .regex(/^avatars\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.jpg$/)
        .nullable()
        .optional(),
});
// POST /v1/profile/photo-upload — request body is empty (uploads are always
// the client-downscaled ~512px JPEG); response carries a short-lived
// presigned PUT URL plus the key the client must confirm via
// PATCH /v1/profile { personalInfo: { photoKey } } after the PUT succeeds
// (ADR-024: confirm-after-upload keeps the DB from ever pointing at an
// object that was never uploaded).
export const ProfilePhotoUploadRequestSchema = z.object({}).strict();
export const ProfilePhotoUploadResponseSchema = z.object({
    uploadUrl: z.string(),
    /** Always `avatars/{userId}/{uuid}.jpg` — scoped to the calling user. */
    photoKey: z.string(),
    expiresAt: IsoDateTimeSchema,
});
export const CalorieTargetSchema = z.object({
    kcal: z.number().int(),
    source: z.enum(["calculated", "manual"]),
    /** Required when a manual target is entered (ADR-011 default floor rule). */
    floorAcknowledged: z.boolean(),
});
/**
 * Write-side variant (ADR-036, review finding 4): a requested target is
 * bounded 800–8000 kcal — even with the below-floor acknowledgement, 0 or
 * 50,000 is a typo, not a choice. The read schema above stays unbounded so
 * a historical out-of-range row can still be served rather than 500ing.
 */
export const CalorieTargetWriteSchema = CalorieTargetSchema.extend({
    kcal: z.number().int().min(800).max(8000),
    /**
     * ADR-036 finding 3: a below-floor manual target needs a SERVER-ISSUED
     * acknowledgement token, not just the boolean — the 422
     * TARGET_BELOW_FLOOR response carries `details.ackToken`; the retry must
     * echo it (single-use, 30-minute expiry, bound to this user + kcal).
     * This makes the disclaimer round-trip structural: no client (or model)
     * can skip it by hardcoding `floorAcknowledged: true`.
     */
    floorAckToken: z.string().optional(),
});
export const MacroTargetsSchema = z.object({
    proteinG: z.number(),
    carbsG: z.number(),
    fatG: z.number(),
    toleranceCaloriesPercent: z.number().default(10),
    toleranceMacrosPercent: z.number().default(15),
});
export const ScheduleEntrySchema = z.object({
    slot: MealSlotSchema,
    maxActiveMinutes: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Activity & energy model (ADR-027 rule 7; migration 0022). Replaces the
// never-actually-wired 4-option "Activity" select (main.html) with a
// structured level + explicit sport log + optional device-reported active
// energy, feeding shared/targets.ts's TDEE estimate.
// ---------------------------------------------------------------------------
/**
 * Daily-movement/occupational activity ONLY — deliberately NOT "how much you
 * exercise" (structured exercise is `sports` below, summed on top so the two
 * never double-count the same calories). `null` = not yet answered
 * (existing users pre-ADR-027; the calculator falls back to its pre-existing
 * fixed multiplier so nothing silently changes for them).
 */
export const ActivityLevelSchema = z.enum(["sedentary", "light", "moderate", "high"]);
export const SportTypeSchema = z.enum([
    "strength",
    "running",
    "cycling",
    "swimming",
    "team_sports",
    "yoga_pilates",
    "walking_hiking",
    "other",
]);
export const SportIntensitySchema = z.enum(["low", "moderate", "high"]);
export const SportEntrySchema = z.object({
    type: SportTypeSchema,
    sessionsPerWeek: z.number().int().min(1).max(14),
    intensity: SportIntensitySchema,
    /** Conservative default assumed when omitted (see shared/targets.ts). */
    minutesPerSession: z.number().int().min(5).max(240).nullable(),
});
export const ActivityInputSchema = z.object({
    level: ActivityLevelSchema.nullable(),
    sports: z.array(SportEntrySchema).max(8),
    /** UX opt-in only ("I have a watch and want to use it"); the calculation
     * itself keys off `avgDeviceActiveKcal` being present, not this flag. */
    wearableKcalEnabled: z.boolean(),
    /** Self-reported average daily ACTIVE energy (already excludes BMR) from
     * the user's own wearable/app. Manual entry only — no device API
     * integration (ADR-027 rule 7, later decision). */
    avgDeviceActiveKcal: z.number().int().min(0).max(3000).nullable(),
});
/**
 * Budget Model v2 (ADR-052, 2026-09-04). Two INDEPENDENT optional amounts:
 *  - `weeklyGoalEurCents` — the SOFT weekly spending goal (an average the
 *    user wants over time; single shops may be above or below it);
 *  - `maxCheckoutEurCents` — the HARD optional maximum per single checkout.
 * `model` is server-derived: "v2" once the user has set/classified their
 * preferences; "legacy_hard_cap" while a pre-v2 weekly budget exists that
 * the user has NOT yet classified (it keeps its old hard-cap behavior
 * internally; both v2 fields are null and `legacyWeeklyLimitEurCents`
 * carries the unclassified amount — it is NEVER presented as two intentional
 * choices); "none" when nothing is set.
 * The pre-v2 pair (`weeklyEurCents` / `hasTarget`) is served read-only for
 * one release as a compatibility mirror of the hard ceiling.
 */
export const BudgetModelSchema = z.enum(["v2", "legacy_hard_cap", "none"]);
export const BudgetPreferencesSchema = z.object({
    model: BudgetModelSchema,
    weeklyGoalEurCents: z.number().int().nonnegative().nullable(),
    maxCheckoutEurCents: z.number().int().nonnegative().nullable(),
    legacyWeeklyLimitEurCents: z.number().int().nonnegative().nullable(),
    /** @deprecated compatibility mirror of the hard ceiling (max or legacy limit). */
    weeklyEurCents: z.number().int().nonnegative().nullable(),
    /** @deprecated compatibility mirror: true when any hard ceiling applies. */
    hasTarget: z.boolean(),
});
/**
 * PATCH shape for the budget: EITHER the v2 pair (sets model "v2" — a
 * legacy account classifies its old amount this way, never silently) OR the
 * pre-v2 pair (unmigrated clients only; keeps the legacy hard-cap meaning).
 * Mixing both is INVALID_BODY.
 */
export const BudgetPatchSchema = z.union([
    z
        .object({
        weeklyGoalEurCents: z.number().int().nonnegative().nullable(),
        maxCheckoutEurCents: z.number().int().nonnegative().nullable(),
    })
        .strict(),
    z
        .object({
        weeklyEurCents: z.number().int().nonnegative().nullable(),
        hasTarget: z.boolean(),
    })
        .strict(),
]);
export const ProfileSchema = z.object({
    revision: z.number().int().nonnegative(),
    goalType: GoalTypeSchema,
    calorieTarget: CalorieTargetSchema,
    macroTargets: MacroTargetsSchema,
    cookingEnjoyment: CookingEnjoymentSchema,
    cookingSkill: CookingSkillSchema,
    planningMode: PlanningModeSchema,
    householdServings: z.number().int().min(1),
    /** ADR-023 snack timing preference; see SnackPreferenceSchema. */
    snackPreference: SnackPreferenceSchema,
    /** ADR-027 rule 7; editable here (PATCH /v1/profile) same as onboarding. */
    activity: ActivityInputSchema,
    budget: BudgetPreferencesSchema,
    preferredRetailers: z.array(z.string()),
    /**
     * ADR-029 offer-alert emails — UWG §7 EXPRESS consent, DEFAULT FALSE.
     * Never inferred, never defaulted on: only an explicit PATCH with
     * `offerEmailsOptIn: true` (Settings toggle) or the tokenized unsubscribe
     * endpoint (sets false) ever changes it. The `.default(false)` here is
     * response-parse leniency for clients ahead of the backend — it can only
     * ever default to the no-consent state.
     */
    offerEmailsOptIn: z.boolean().default(false),
    schedule: z.array(ScheduleEntrySchema),
});
// ---------------------------------------------------------------------------
// Health conditions (ADR-013 Decision 3) — declared during onboarding step 13
// (schemas/onboarding.ts). Never unlocks therapeutic diet planning; drives only
// (a) food-safety exclusions derived server-side (services/db conditionDerivations),
// (b) disclaimer surfaces, (c) unsafe-goal blocking. Read-only on the profile
// response — deliberately NOT part of ProfileSchema/ProfilePatchRequestSchema
// above, so a generic PATCH /v1/profile can never mutate a safety-relevant
// declaration; the dedicated onboarding-step flow is the only write path,
// mirroring how hard constraints get their own confirmation command instead of
// riding the generic profile patch.
// ---------------------------------------------------------------------------
export const HealthConditionSchema = z.enum([
    "pregnancy",
    "breastfeeding",
    "type2_diabetes",
    "hypertension",
    "kidney_disease",
    "celiac",
    "eating_disorder_history",
    "other",
]);
export const HealthConditionEntrySchema = z.object({
    condition: HealthConditionSchema,
    otherText: z.string().nullable(),
    medications: z.array(z.string()),
    declaredAt: IsoDateTimeSchema,
});
// GET /v1/profile response
export const ProfileResponseSchema = ProfileSchema.extend({
    /** Personal record (users table, migration 0021) — fields null until the user provides them. */
    personalInfo: PersonalInfoSchema,
    healthConditions: z.array(HealthConditionEntrySchema),
});
// PATCH /v1/profile request — explicit partial update, no user_id, no revision
// bump here (profile fields are not the revisioned aggregate; hard constraints
// have their own dedicated confirmation command below). Deliberately derived
// from ProfileSchema (not ProfileResponseSchema) so healthConditions cannot be
// smuggled through this generic patch — see the note above.
// `personalInfo` uses its own dedicated patch shape (PersonalInfoPatchSchema)
// rather than deriving from PersonalInfoSchema: email must be unreachable
// from writes, and the write-side photo field is the S3 `photoKey`, not the
// read-side presigned `photoUrl`.
export const ProfilePatchRequestSchema = ProfileSchema.omit({ revision: true })
    .partial()
    .extend({
    personalInfo: PersonalInfoPatchSchema.optional(),
    // ADR-036: writes take the BOUNDED target variant (800–8000 kcal).
    calorieTarget: CalorieTargetWriteSchema.optional(),
    // Budget Model v2: writes take the v2 pair or the legacy pair, never both.
    budget: BudgetPatchSchema.optional(),
    // Plain optional boolean (NOT the response schema's `.default(false)`):
    // an omitted key must stay omitted on PATCH — materializing a default
    // here would silently revoke consent on every unrelated profile save.
    offerEmailsOptIn: z.boolean().optional(),
});
// ---------------------------------------------------------------------------
// PUT /v1/profile/hard-constraints — dedicated confirmation flow
// ---------------------------------------------------------------------------
export const HardConstraintTypeSchema = z.enum([
    "allergen",
    "intolerance",
    "dietary_rule",
    "never_eat",
    "safety_floor",
]);
/**
 * ADR-080: an intolerance is not an allergy. A STRUCTURED intolerance (lactose today)
 * carries the user-chosen level it is enforced at — a hard constraint set only in
 * onboarding step 6 and the confirmed Food & health flow, never in chat; the user's
 * own assertion, never a diagnosis.
 *  - avoid_all_dairy: enforced like the milk allergen (contains / may_contain / unknown
 *    block) but named as the lactose intolerance;
 *  - lactose_free_ok: a milk-blocking ingredient passes only when sourced catalog data
 *    marks it lactose-free; unknown still blocks. A declared milk ALLERGY is never
 *    satisfied by a lactose-free product.
 */
export const LactoseLevelSchema = z.enum(["avoid_all_dairy", "lactose_free_ok"]);
export const LACTOSE_LEVELS = LactoseLevelSchema.options;
/** Intolerance subjects that carry a level (everything else is free text on the intolerance kind). */
export const STRUCTURED_INTOLERANCE_SUBJECTS = ["lactose"];
/**
 * ADR-080: the free-text phrasings (DE + EN, lower-cased, trimmed) that NAME the structured
 * lactose subject. ONE list for the API vocabulary (a typed statement becomes the structured
 * rule, never the milk allergen), migration 0045 (pinned equal by a test) and both clients
 * (the free-text boxes redirect these to the "Lactose intolerance" choice).
 */
export const STRUCTURED_INTOLERANCE_PHRASINGS = {
    "lactose": "lactose",
    "laktose": "lactose",
    "lactose intolerance": "lactose",
    "lactose-intolerant": "lactose",
    "lactose intolerant": "lactose",
    "laktoseintoleranz": "lactose",
    "laktoseintolerant": "lactose",
    "laktoseunverträglichkeit": "lactose",
    "laktoseunvertraeglichkeit": "lactose",
    "laktose-intoleranz": "lactose",
};
/** The structured subject a free-text value names, or null (trim + lower-case). */
export function normalizeStructuredIntoleranceText(value) {
    return STRUCTURED_INTOLERANCE_PHRASINGS[value.trim().toLowerCase()] ?? null;
}
/**
 * A row whose meaning the user has not confirmed since ADR-080 (migration 0045):
 *  - milk_or_lactose: the old "Milk / lactose" chip wrote a milk ALLERGY — allergy or intolerance?
 *  - lactose_level:   free-text "lactose" became the strictest level — is lactose-free dairy fine?
 * The safer reading holds until answered through the confirmed PUT (`resolveClarifications`).
 */
export const HardConstraintClarificationSchema = z.enum(["milk_or_lactose", "lactose_level"]);
export const HardConstraintSchema = z.object({
    type: HardConstraintTypeSchema,
    subjectId: z.string(),
    status: z.enum(["active", "removed"]),
    /** Required when `type` is `intolerance` and `subjectId` is a structured subject; forbidden otherwise (server-enforced). */
    level: LactoseLevelSchema.optional(),
});
/** The EU-14 allergen chips both clients render — ONE source (ADR-080 replaced four client copies). `labelKey` indexes `onboarding.step6.allergens.*`. */
export const EU14_ALLERGEN_OPTIONS = [
    { value: "peanuts", labelKey: "peanut" },
    { value: "nuts", labelKey: "treeNut" },
    { value: "milk", labelKey: "milk" },
    { value: "gluten", labelKey: "gluten" },
    { value: "eggs", labelKey: "egg" },
    { value: "fish", labelKey: "fish" },
    { value: "crustaceans", labelKey: "shellfish" },
    { value: "soybeans", labelKey: "soy" },
    { value: "sesame", labelKey: "sesame" },
    { value: "celery", labelKey: "celery" },
    { value: "mustard", labelKey: "mustard" },
    { value: "sulphites", labelKey: "sulphites" },
    { value: "lupin", labelKey: "lupin" },
    { value: "molluscs", labelKey: "mollusc" },
];
export const HardConstraintsPutRequestSchema = z.object({
    baseRevision: z.number().int().nonnegative(),
    constraints: z.array(HardConstraintSchema),
    /** Explicit re-confirmation required because this can invalidate the current plan. */
    confirmed: z.literal(true),
    /** ADR-080: the pending clarifications this confirmed write answers (the new set must settle each one). */
    resolveClarifications: z.array(HardConstraintClarificationSchema).optional(),
});
export const HardConstraintClarificationEntrySchema = z.object({
    kind: HardConstraintClarificationSchema,
    constraint: z.object({ type: HardConstraintTypeSchema, subjectId: z.string() }),
});
export const HardConstraintsResponseSchema = z.object({
    revision: z.number().int().nonnegative(),
    constraints: z.array(HardConstraintSchema),
    currentPlanInvalidated: z.boolean(),
    /** ADR-080: rows the user still has to confirm (empty for everyone onboarded after ADR-080). */
    clarifications: z.array(HardConstraintClarificationEntrySchema).default([]),
});
//# sourceMappingURL=profile.js.map